import { Box, Typography } from '@mui/material';
import { CSSObject, styled, Theme } from '@mui/material/styles';
import { NavLink } from 'react-router-dom';

import { fastTransition } from '../../styles/tokens';

/**
 * Shell (guide 3 and 7.1): neutral sidebar of 240px (64px collapsed) on the
 * canvas, hairline on the right, logo 28px, 36px items, active item with a
 * subtle neutral background. Never painted in the brand color.
 */
export const Sidebar = styled('nav', {
  shouldForwardProp: (prop) => prop !== 'open',
})<{ open: boolean }>(({ theme, open }) => {
  const { layout, motion, space } = theme.palette.tokens;
  return {
    position: 'fixed',
    top: 0,
    left: 0,
    bottom: 0,
    boxSizing: 'border-box',
    zIndex: theme.zIndex.appBar,
    width: open ? layout.sidebarWidth : layout.sidebarCollapsed,
    display: 'flex',
    flexDirection: 'column',
    backgroundColor: theme.palette.background.default,
    borderRight: `1px solid ${theme.palette.divider}`,
    padding: `${space[4]}px ${space[3]}px`,
    transition: `width ${motion.base} ${motion.easeOut}`,
    overflow: 'hidden',
  };
});

export const LogoRow = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'open',
})<{ open: boolean }>(({ theme, open }) => ({
  display: 'flex',
  flexDirection: open ? 'row' : 'column',
  gap: open ? 0 : theme.palette.tokens.space[2],
  alignItems: 'center',
  justifyContent: open ? 'space-between' : 'center',
  minHeight: '40px',
  padding: open
    ? `0 ${theme.palette.tokens.space[2]}px`
    : `0 ${theme.palette.tokens.space[1]}px`,
  marginBottom: theme.palette.tokens.space[4],
}));

/** Wraps the expanded lockup so the SVG sits on the row baseline. */
export const Logo = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  paddingLeft: theme.palette.tokens.space[1],
  '& svg': { display: 'block' },
}));

/**
 * Collapsed state: the hub mark doubles as the "expand" affordance. A real
 * button so it is reachable by keyboard, unlike a clickable image.
 */
export const LogoMarkButton = styled('button')(({ theme }) => {
  const { radius, motion } = theme.palette.tokens;
  return {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '32px',
    height: '32px',
    padding: 0,
    border: 0,
    borderRadius: `${radius.control}px`,
    backgroundColor: 'transparent',
    cursor: 'pointer',
    transition: `background-color ${motion.fast} ${motion.easeOut}`,
    '&:hover': { backgroundColor: theme.palette.action.hover },
    '& svg': { display: 'block', borderRadius: '4px' },
  };
});

export const NavList = styled('ul')(({ theme }) => ({
  listStyle: 'none',
  margin: 0,
  padding: 0,
  display: 'flex',
  flexDirection: 'column',
  gap: '2px',
  flex: 1,
  minHeight: 0,
  overflowY: 'auto',
  paddingTop: theme.palette.tokens.space[2],
  borderTop: `1px solid ${theme.palette.divider}`,
}));

const navItemStyle = (
  theme: Theme,
  active: boolean,
  open: boolean
): CSSObject => {
  const { color, type, font, radius, motion } = theme.palette.tokens;
  return {
    display: 'flex',
    alignItems: 'center',
    justifyContent: open ? 'flex-start' : 'center',
    gap: '10px',
    width: '100%',
    height: '36px',
    padding: open ? '8px 12px' : '8px 0',
    border: 0,
    borderRadius: `${radius.control}px`,
    textDecoration: 'none',
    color: active ? color.textPrimary : color.textSecondary,
    backgroundColor: active ? color.surfaceHover : 'transparent',
    fontFamily: 'inherit',
    fontSize: `${type.ui.size}px`,
    lineHeight: type.ui.lineHeight,
    fontWeight: active ? font.weightMedium : font.weightRegular,
    letterSpacing: type.ui.tracking,
    whiteSpace: 'nowrap',
    textAlign: 'left',
    transition: fastTransition(motion, 'background-color', 'color'),
    cursor: 'pointer',
    '&:hover': {
      backgroundColor: color.surfaceHover,
      color: color.textPrimary,
    },
    '&:focus-visible': {
      outline: 'none',
      boxShadow: color.focusRing,
    },
    '& .material-symbols-outlined': {
      fontSize: '20px',
      flexShrink: 0,
      color: 'inherit',
      fontVariationSettings: '"FILL" 0, "wght" 400, "GRAD" 0, "opsz" 20',
    },
  };
};

export const NavItem = styled(NavLink, {
  shouldForwardProp: (prop) => prop !== 'active' && prop !== 'open',
})<{ active: boolean; open: boolean }>(({ theme, active, open }) =>
  navItemStyle(theme, active, open)
);

export const NavButton = styled('button', {
  shouldForwardProp: (prop) => prop !== 'active' && prop !== 'open',
})<{ active: boolean; open: boolean }>(({ theme, active, open }) =>
  navItemStyle(theme, active, open)
);

export const NavLabel = styled('span')(() => ({
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  flex: 1,
}));

export const CountBadge = styled('span')(({ theme }) => {
  const { color, radius, font } = theme.palette.tokens;
  return {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    minWidth: '18px',
    height: '18px',
    padding: '0 6px',
    borderRadius: `${radius.pill}px`,
    backgroundColor: color.actionTint,
    color: color.textPrimary,
    fontSize: '11px',
    fontWeight: font.weightMedium,
    lineHeight: 1,
  };
});

export const CollapsedDot = styled('span')(({ theme }) => ({
  position: 'absolute',
  top: '6px',
  right: '10px',
  width: '8px',
  height: '8px',
  borderRadius: '50%',
  backgroundColor: theme.palette.tokens.color.action,
  border: `2px solid ${theme.palette.background.default}`,
}));

export const NavItemWrap = styled('li')(() => ({
  position: 'relative',
}));

export const SidebarFooter = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  flexShrink: 0,
  gap: '2px',
  paddingTop: theme.palette.tokens.space[3],
  borderTop: `1px solid ${theme.palette.divider}`,
}));

export const UserRow = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'open',
})<{ open: boolean }>(({ theme, open }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: open ? 'flex-start' : 'center',
  gap: '10px',
  padding: open ? '8px 12px' : '8px 0',
  minHeight: '44px',
  borderRadius: `${theme.palette.tokens.radius.control}px`,
}));

export const Avatar = styled('span')(({ theme }) => {
  const { color, font } = theme.palette.tokens;
  return {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '28px',
    height: '28px',
    borderRadius: '50%',
    backgroundColor: color.n150,
    color: color.textPrimary,
    fontSize: '11px',
    fontWeight: font.weightMedium,
    letterSpacing: '0.02em',
    flexShrink: 0,
  };
});

export const UserText = styled(Box)(() => ({
  display: 'flex',
  flexDirection: 'column',
  minWidth: 0,
  flex: 1,
}));

export const UserName = styled(Typography)(({ theme }) => ({
  ...theme.typography.body2,
  fontWeight: theme.palette.tokens.font.weightMedium,
  color: theme.palette.text.primary,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
}));

export const UserEmail = styled(Typography)(({ theme }) => ({
  ...theme.typography.caption,
  color: theme.palette.text.secondary,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
}));

export const ToggleButton = styled('button', {
  shouldForwardProp: (prop) => prop !== 'open',
})<{ open: boolean }>(({ theme, open }) => {
  const { color, radius, motion } = theme.palette.tokens;
  return {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: open ? '32px' : '100%',
    height: '32px',
    border: 0,
    borderRadius: `${radius.control}px`,
    backgroundColor: 'transparent',
    color: color.textSecondary,
    cursor: 'pointer',
    transition: `background-color ${motion.fast} ${motion.easeOut}`,
    '&:hover': {
      backgroundColor: color.surfaceHover,
      color: color.textPrimary,
    },
    '&:focus-visible': { outline: 'none', boxShadow: color.focusRing },
    '& .material-symbols-outlined': { fontSize: '20px' },
  };
});

export const OutletContainer = styled('main', {
  shouldForwardProp: (prop) => prop !== 'open',
})<{ open: boolean }>(({ theme, open }) => {
  const { layout, motion } = theme.palette.tokens;
  return {
    marginLeft: open ? layout.sidebarWidth : layout.sidebarCollapsed,
    minHeight: '100vh',
    transition: `margin-left ${motion.base} ${motion.easeOut}`,
    backgroundColor: theme.palette.background.default,
  };
});
